import React, { useState, useEffect } from "react";
import SEO from "../seo";
const OnlineStore = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["overview", "features", "conclusion"];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Online Store – Coming Soon | xPage Help Center"
        description="Learn about xPage's upcoming Online Store features, including customization, storefront management, and easy setup to grow your online business."
        keywords="xPage, Online Store, eCommerce, Landing Page, Storefront, Features, Product Management"
        canonical="https://help.xpage.ai/help/landing-page-account/Online%20Store"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header className="mb-6">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Online Store – Coming Soon
          </h1>
          <p className="text-sm text-systemLabel">
            Get ready to take your landing pages further.
          </p>
        </header>

        {/* Overview Section */}
        <section id="overview" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Overview
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              We're building a complete Online Store experience right inside
              xPage, designed to help you launch, manage, and scale your
              products with the same simplicity and flexibility you already
              love.
            </p>
            <p className="text-sm text-systemLabel">
              The new Online Store will include everything you need to go from
              idea to sale:
            </p>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Features
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Feature
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Description
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Customize
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Design your storefront exactly the way you want using our
                    intuitive, no-code editor.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">Pages</strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Create and organize product and content pages with seamless
                    navigation.
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">Menu</strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Structure your store layout and improve how customers browse
                    your products.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Preferences
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Set up your store settings, currency, language, and more —
                    all in one place.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Conclusion Section */}
        <section id="conclusion" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Conclusion
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel">
              Whether you're launching your first product or managing a growing
              catalog, xPage will make it effortless to build and run your
              store, visually, quickly, and without complexity.
            </p>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#features"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "features"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Features
              </a>
            </li>
            <li>
              <a
                href="#conclusion"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "conclusion"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Conclusion
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default OnlineStore;
